import {
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassSelect,
} from '@merritrac/shared/components';
import { AlertCircle, ArrowLeft, CheckCircle2, Loader2, Paperclip, Send } from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { useGraphQL } from '../hooks/useGraphQL.js';
import { useGraphQLMutation } from '../hooks/useGraphQLMutation.js';
import { usePortalAuth } from '../hooks/usePortalAuth.js';

import type { ReactElement } from 'react';

/* ── GraphQL ── */

const GET_MY_CONTACT = `
  query GetMyContact($id: Int!) {
    unitContact(id: $id) {
      id
      propertyId
      unitAk
      property { name }
    }
  }
`;

const CREATE_SERVICE_REQUEST = `
  mutation CreateServiceRequest(
    $propertyId: Int!
    $unitAk: Float
    $description: String!
    $urgency: String
  ) {
    createServiceRequest(
      propertyId: $propertyId
      unitAk: $unitAk
      description: $description
      urgency: $urgency
    ) {
      id
      status
    }
  }
`;

/* ── Response types ── */

interface UnitContactData {
  unitContact: {
    id: number;
    propertyId: number;
    unitAk: number | null;
    property: { name: string } | null;
  } | null;
}

interface CreateServiceRequestData {
  createServiceRequest: { id: string; status: string };
}

/* ── Urgency options ── */

const urgencyOptions: readonly { value: string; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low — not time-sensitive' },
  { value: 'urgent', label: 'Urgent — needs prompt attention' },
];

/* ── Page ── */

export function NewRequestPage(): ReactElement {
  const navigate = useNavigate();
  const contactId = usePortalAuth((s) => s.user?.contactId ?? null);

  /* Contact / property info.
   * The hook always fires; when contactId is null we pass id: 0 which will
   * return null from the server. The render path checks contactId first and
   * shows the "not linked" UI before the query result matters. */
  const {
    data: contactData,
    loading: contactLoading,
    error: contactError,
  } = useGraphQL<UnitContactData>(
    GET_MY_CONTACT,
    contactId !== null ? { id: contactId } : undefined,
  );

  /* Form state */
  const [description, setDescription] = useState('');
  const [urgency, setUrgency] = useState('normal');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [descriptionError, setDescriptionError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* Mutation */
  const {
    mutate: createRequest,
    loading: submitting,
    error: submitError,
  } = useGraphQLMutation<CreateServiceRequestData>(CREATE_SERVICE_REQUEST);

  function handleBack(): void {
    void navigate('/my-requests');
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0] ?? null;
    setPhotoFile(file);
  }

  function removePhoto(): void {
    setPhotoFile(null);
    if (fileInputRef.current !== null) {
      fileInputRef.current.value = '';
    }
  }

  async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setDescriptionError('');

    const trimmed = description.trim();
    if (trimmed === '') {
      setDescriptionError('Please describe what you need help with.');
      return;
    }

    const contact = contactData?.unitContact;
    if (contact === null || contact === undefined) {
      return; // guard — button is disabled anyway
    }

    const variables: Record<string, unknown> = {
      propertyId: contact.propertyId,
      description: trimmed,
      urgency,
    };
    if (contact.unitAk !== null) {
      variables['unitAk'] = contact.unitAk;
    }

    const result = await createRequest(variables);
    if (result !== null) {
      setSubmitted(true);
    }
  }

  /* ── Contact loading / error states ── */
  if (contactId === null) {
    return (
      <div className="space-y-6 max-w-xl mx-auto">
        <div>
          <GlassButton variant="ghost" size="sm" onClick={handleBack} className="-ml-1">
            <ArrowLeft size={14} />
            My Requests
          </GlassButton>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning-fg">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <p>Your account is not linked to a unit. Please contact your property manager.</p>
        </div>
      </div>
    );
  }

  /* ── Success screen ── */
  if (submitted) {
    return (
      <div className="space-y-6 max-w-xl mx-auto">
        <div>
          <GlassButton variant="ghost" size="sm" onClick={handleBack} className="-ml-1">
            <ArrowLeft size={14} />
            My Requests
          </GlassButton>
        </div>
        <GlassCard>
          <GlassCardContent className="py-10">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center">
                <CheckCircle2 size={32} className="text-success" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-text">Request Submitted</h2>
                <p className="text-sm text-text-secondary mt-1 max-w-xs">
                  Your request has been received. We&apos;ll review it and follow up with you
                  shortly.
                </p>
              </div>
              <GlassButton variant="primary" size="md" onClick={handleBack}>
                View My Requests
              </GlassButton>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>
    );
  }

  const contact = contactData?.unitContact ?? null;
  const canSubmit = !contactLoading && contact !== null && !submitting;

  return (
    <div className="space-y-6 max-w-xl mx-auto">
      {/* Back navigation */}
      <div>
        <GlassButton variant="ghost" size="sm" onClick={handleBack} className="-ml-1">
          <ArrowLeft size={14} />
          My Requests
        </GlassButton>
      </div>

      {/* Contact error */}
      {contactError !== null && (
        <div className="flex items-start gap-3 rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="font-medium">Could not load your unit information</p>
            <p className="text-xs mt-0.5 text-danger/80">{contactError}</p>
          </div>
        </div>
      )}

      {/* Submit error */}
      {submitError !== null && (
        <div className="flex items-start gap-3 rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="font-medium">Submission failed</p>
            <p className="text-xs mt-0.5 text-danger/80">{submitError}</p>
          </div>
        </div>
      )}

      {/* Form card */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex flex-col gap-1">
            <h1 className="text-lg font-semibold text-text">Submit a Service Request</h1>
            {contactLoading ? (
              <div className="flex items-center gap-1.5 text-sm text-text-tertiary">
                <Loader2 size={12} className="animate-spin" />
                Loading your unit info&hellip;
              </div>
            ) : contact !== null ? (
              <div className="flex items-center gap-2 flex-wrap">
                {contact.property !== null && (
                  <p className="text-sm text-text-secondary">{contact.property.name}</p>
                )}
                {contact.unitAk !== null && (
                  <GlassBadge variant="default">Unit {String(contact.unitAk)}</GlassBadge>
                )}
              </div>
            ) : null}
          </div>
        </GlassCardHeader>

        <GlassCardContent>
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
            {/* Description */}
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="request-description"
                className="text-sm font-medium text-text-secondary"
              >
                Description{' '}
                <span className="text-danger" aria-hidden="true">
                  *
                </span>
              </label>
              <textarea
                id="request-description"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (descriptionError !== '') {
                    setDescriptionError('');
                  }
                }}
                rows={5}
                placeholder="Describe the issue or what you need help with\u2026"
                aria-describedby={descriptionError !== '' ? 'description-error' : undefined}
                aria-invalid={descriptionError !== '' ? 'true' : undefined}
                className={[
                  'w-full rounded-lg border px-3 py-2.5 text-sm text-text',
                  'bg-[var(--glass-bg)] backdrop-blur-[var(--glass-blur-subtle)]',
                  'placeholder:text-text-tertiary resize-y transition-all',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary',
                  descriptionError !== ''
                    ? 'border-danger focus:ring-danger/30'
                    : 'border-[var(--glass-border)]',
                ].join(' ')}
              />
              {descriptionError !== '' && (
                <p id="description-error" className="text-xs text-danger">
                  {descriptionError}
                </p>
              )}
            </div>

            {/* Urgency */}
            <GlassSelect
              label="Urgency"
              id="request-urgency"
              options={urgencyOptions}
              value={urgency}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setUrgency(e.target.value);
              }}
            />

            {/* Photo attachment */}
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-text-secondary">
                Photo <span className="text-text-tertiary font-normal">(optional)</span>
              </span>
              {photoFile !== null ? (
                <div className="flex items-center gap-3 rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-2.5">
                  <Paperclip size={14} className="text-text-tertiary shrink-0" />
                  <span className="text-sm text-text truncate flex-1">{photoFile.name}</span>
                  <button
                    type="button"
                    onClick={removePhoto}
                    className="text-xs text-text-tertiary hover:text-danger transition-colors shrink-0"
                    aria-label="Remove photo"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    fileInputRef.current?.click();
                  }}
                  className={[
                    'flex items-center gap-2 w-full rounded-lg border border-dashed px-3 py-4',
                    'border-[var(--glass-border)] bg-[var(--glass-bg)] text-text-tertiary',
                    'hover:border-primary/50 hover:text-text-secondary transition-all',
                    'text-sm text-center justify-center',
                  ].join(' ')}
                  aria-label="Attach a photo"
                >
                  <Paperclip size={14} />
                  Tap to attach a photo
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileChange}
                className="sr-only"
                aria-hidden="true"
                tabIndex={-1}
              />
              <p className="text-xs text-text-tertiary">
                Attach a photo to help us understand the issue.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end pt-1">
              <GlassButton
                type="button"
                variant="secondary"
                size="md"
                onClick={handleBack}
                disabled={submitting}
              >
                Cancel
              </GlassButton>
              <GlassButton type="submit" variant="primary" size="md" disabled={!canSubmit}>
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Submitting&hellip;
                  </>
                ) : (
                  <>
                    <Send size={14} />
                    Submit Request
                  </>
                )}
              </GlassButton>
            </div>
          </form>
        </GlassCardContent>
      </GlassCard>
    </div>
  );
}
